import { ElementType, HTMLAttributes, useMemo } from 'react';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AddIcon from '@mui/icons-material/Add';
import CreateIcon from '@mui/icons-material/Create';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PersonPinOutlinedIcon from '@mui/icons-material/PersonPinOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import SendIcon from '@mui/icons-material/Send';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import Loading from '@/components/general/Loading';
import IconButton from '@/components/IconButton';
import NewUserFormModal from '@/components/NewUserFormModal';
import Pagination, { PaginationTypes } from '@/trio-ui/Pagination';
import Table, { Td, Th, Tr } from '@/trio-ui/Table';
import { formattedRoles } from '@/types';
import { User } from '@/types/api';
import { rolesColors } from './UsersManagement.constants';
import * as Styles from './UsersManagement.styles';

const categories = ['PEOPLE', 'HIRING', 'ACADEMY', 'SEO', 'FINANCE'] as const;
const iconByCategory: {
  [key in (typeof categories)[number]]: ElementType<Pick<HTMLAttributes<SVGElement>, 'style'>>;
} = {
  PEOPLE: PersonPinOutlinedIcon,
  HIRING: TravelExploreIcon,
  ACADEMY: SchoolOutlinedIcon,
  SEO: InsightsIcon,
  FINANCE: AccountBalanceOutlinedIcon,
};

type UsersManagement = PaginationTypes & {
  users?: User[];
  isLoading?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  isNewUserFormModalOpen?: boolean;
  refreshUsersList?: () => void;
  onEditUserClick?: (team: User) => void;
  onResendInvite?: (user: User) => void;
  onAddUserClick?: () => void;
  onCloseForm?: () => void;
  editingUser?: User;
};

const UsersManagement = function ({
  users = [],
  isLoading = false,
  onChangePageHandler,
  onChangeRecordsPerPage,
  count,
  itemsPerPage,
  lastPage,
  currentPage,
  search,
  onSearchChange,
  isNewUserFormModalOpen = false,
  onAddUserClick,
  refreshUsersList,
  onEditUserClick,
  onResendInvite,
  onCloseForm,
  editingUser,
}: UsersManagement): JSX.Element {
  const renderHeader = useMemo(() => {
    return (
      <Tr>
        <Th>Profile</Th>
        <Th>Roles</Th>
        <Th>
          <MoreHorizIcon className="-tw-my-[3.6px]" />
        </Th>
      </Tr>
    );
  }, []);

  const renderRow = (data: User): JSX.Element => {
    const hasMissingPerson = !data.person?.id;

    return (
      <Tr key={data.id}>
        <Td>
          <div className="tw-flex tw-flex-col tw-gap-1">
            <p className="tw-text-neutral-100 tw-text-lg tw-font-semibold tw-block tw-leading-tight">
              {data.name}
            </p>
            <p className="tw-text-neutral-100 tw-text-sm tw-block tw-leading-normal tw-normal-case">
              {data.email}
            </p>
          </div>
        </Td>
        <Td>
          <div className="tw-flex tw-flex-col tw-gap-[3px]">
            {data.isOwner ? (
              <span className="tw-inline-block tw-text-xs tw-font-semibold tw-px-2 tw-py-[3px] tw-rounded tw-text-white tw-bg-[#054AD9] tw-w-fit">
                Owner
              </span>
            ) : (
              categories.map((category) => {
                const roles = data.roles.filter((role) => role.value.startsWith(category));
                const Icon = iconByCategory[category];

                if (roles.length === 0) return null;

                return (
                  <div className="tw-flex tw-gap-2 tw-items-start" key={category}>
                    <Icon style={{ width: 15, height: 15, marginTop: 3.5 }} />
                    <p className="tw-text-neutral-100 tw-text-sm tw-font-semibold tw-capitalize tw-leading-[22px]">
                      {category.toLowerCase()}
                    </p>
                    <div className="tw-flex tw-gap-1.5 tw-flex-wrap">
                      {roles.map((role) => {
                        return (
                          <p
                            key={role.id}
                            className="tw-text-neutral-100 tw-text-xs tw-block tw-px-1.5 tw-py-[3px] tw-rounded tw-capitalize tw-bg-neutral-30"
                            style={{ backgroundColor: rolesColors[role.value] }}
                          >
                            {formattedRoles[role.value]}
                          </p>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Td>
        <Td className="tw-w-0">
          {!data.isOwner && (
            <div className="tw-flex tw-items-center tw-gap-1">
              <IconButton onClick={() => onResendInvite?.(data)} icon={SendIcon} />
              <IconButton onClick={() => onEditUserClick?.(data)} icon={CreateIcon} />
              {hasMissingPerson && (
                <span
                  className="tw-text-xs tw-font-semibold tw-leading-none"
                  data-testid={`missing-person-indicator-user-${data.id}`}
                >
                  X
                </span>
              )}
            </div>
          )}
        </Td>
      </Tr>
    );
  };

  return (
    <div data-testid="staff-page">
      <div className="tw-flex tw-items-center tw-gap-8 tw-mb-4">
        <p className="tw-text-neutral-100 tw-text-lg tw-font-semibold tw-grow">Users</p>
        <Styles.Search value={search} onChangeHandler={(e) => onSearchChange(e.target.value)} />
        <Styles.AddNew aria-label="New User" onClick={onAddUserClick}>
          <AddIcon />
        </Styles.AddNew>
      </div>

      {isLoading ? (
        <Loading fullPage />
      ) : (
        <>
          <Styles.TableContainer>
            <Table<User>
              renderHeader={renderHeader}
              renderRow={renderRow}
              data={users}
              customBorderRadius={8}
              renderEmptyState={
                <Tr>
                  <Td
                    colSpan={3}
                    textAlign="center"
                    padding="28px 74px"
                    fontSize={14}
                    lineHeight="150%"
                  >
                    No users found.
                  </Td>
                </Tr>
              }
            />
          </Styles.TableContainer>
          <Pagination
            onChangeRecordsPerPage={onChangeRecordsPerPage}
            lastPage={lastPage}
            itemsPerPage={itemsPerPage}
            count={count}
            currentPage={currentPage}
            onChangePageHandler={onChangePageHandler}
          />
        </>
      )}
      <NewUserFormModal
        isOpen={isNewUserFormModalOpen}
        onClose={onCloseForm}
        onInviteNewUser={refreshUsersList}
        onEditUser={refreshUsersList}
        onRemoveUser={refreshUsersList}
        editing={editingUser}
      />
    </div>
  );
};

export default UsersManagement;
